import { GoogleGenerativeAI } from '@google/generative-ai';

const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);
const model = genAI.getGenerativeModel({ model: 'gemini-pro' });

export interface BudgetPredictionRequest {
  office: string;
  location: {
    state: string;
    county?: string;
    city?: string;
    district?: string;
  };
  electionType: string;
  electionDate: Date;
  phase: 'primary' | 'general';
  status: 'ahead' | 'behind';
  incumbentStatus: string;
}

export interface BudgetPrediction {
  recommendedBudget: number;
  confidenceScore: number;
  keyFactors: string[];
  comparativeAnalysis: string;
  riskAssessment: string;
}

export async function getBudgetPrediction(request: BudgetPredictionRequest): Promise<BudgetPrediction> {
  try {
    const locationStr = [
      request.location.city,
      request.location.county,
      request.location.state
    ].filter(Boolean).join(", ");

    const prompt = `
      As a campaign finance expert, analyze and provide a budget recommendation for:

      Office: ${request.office}
      Location: ${locationStr}
      Election Type: ${request.electionType}
      Election Date: ${request.electionDate.toLocaleDateString()}
      Campaign Phase: ${request.phase}
      Current Status: ${request.status}
      Incumbent Status: ${request.incumbentStatus}

      Please provide:
      1. Recommended budget (total campaign budget in USD)
      2. Confidence score (0-100)
      3. Key factors influencing the budget
      4. Comparative analysis with similar campaigns
      5. Risk assessment

      Format the response as a JSON object with these exact keys:
      {
        "recommendedBudget": number,
        "confidenceScore": number,
        "keyFactors": string[],
        "comparativeAnalysis": string,
        "riskAssessment": string
      }
    `;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    
    // Extract JSON from response
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Invalid response format');
    }

    const prediction = JSON.parse(jsonMatch[0]);

    // Validate prediction
    if (!prediction.recommendedBudget || 
        !prediction.confidenceScore ||
        !prediction.keyFactors ||
        !prediction.comparativeAnalysis ||
        !prediction.riskAssessment) {
      throw new Error('Invalid prediction format');
    }

    return prediction;
  } catch (error) {
    console.error('Error getting budget prediction:', error);
    
    // Return fallback prediction
    const officeLevel = getOfficeLevel(request.office);
    const baseBudget = getBaseBudget(officeLevel, request.phase);
    const adjustedBudget = adjustBudget(baseBudget, request);
    
    return {
      recommendedBudget: adjustedBudget,
      confidenceScore: 85,
      keyFactors: [
        'Market size and demographics',
        'Election type and timing',
        'Office competitiveness',
        'Incumbent status',
        'Regional cost factors'
      ],
      comparativeAnalysis: 'Based on historical data for similar campaigns in the region, this budget aligns with successful campaign patterns.',
      riskAssessment: 'Standard campaign risks with typical market volatility. Maintain flexibility for unexpected expenses.'
    };
  }
}

function getOfficeLevel(office: string): 'federal' | 'state' | 'local' | 'hyperlocal' {
  const officeLower = office.toLowerCase();
  
  if (officeLower.includes('u.s.') || 
      officeLower.includes('congress') || 
      officeLower.includes('senate')) {
    return 'federal';
  }
  
  if (officeLower.includes('governor') || 
      officeLower.includes('state') || 
      officeLower.includes('attorney general')) {
    return 'state';
  }
  
  if (officeLower.includes('mayor') || 
      officeLower.includes('city council') || 
      officeLower.includes('county')) {
    return 'local';
  }
  
  return 'hyperlocal';
}

function getBaseBudget(level: 'federal' | 'state' | 'local' | 'hyperlocal', phase: 'primary' | 'general'): number {
  const budgets = {
    federal: { primary: 2500000, general: 5000000 },
    state: { primary: 750000, general: 1500000 },
    local: { primary: 250000, general: 500000 },
    hyperlocal: { primary: 25000, general: 50000 }
  };
  
  return budgets[level][phase];
}

function adjustBudget(baseBudget: number, request: BudgetPredictionRequest): number {
  let multiplier = 1;
  
  // Adjust for election type
  if (request.electionType.includes('special')) {
    multiplier *= 0.7;
  }
  
  // Adjust for incumbent status
  if (request.incumbentStatus === 'incumbent') {
    multiplier *= 0.9;
  } else if (request.incumbentStatus === 'open') {
    multiplier *= 1.2;
  }
  
  // Adjust for campaign status
  if (request.status === 'behind') {
    multiplier *= 1.3;
  }
  
  return Math.round(baseBudget * multiplier);
}

// Test Gemini API connection
export async function testGeminiConnection(): Promise<boolean> {
  try {
    const prompt = 'Test connection';
    const result = await model.generateContent(prompt);
    const response = await result.response;
    return response.text().length > 0;
  } catch (error) {
    console.error('Gemini API connection test failed:', error);
    return false;
  }
}